import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Popper from '@mui/material/Popper';
import type { SxProps, Theme } from '@mui/material/styles';
import { type UseAutocompleteReturnValue } from '@mui/material/useAutocomplete';
import { HTMLAttributes, ReactNode, useLayoutEffect, useState } from 'react';

import { POPPER_Z_INDEX } from '@/core/constants/layout';
import { mergeSx } from '@/utils/mergeSx';

import { DropdownItemBase } from '../autocomplete.types';

import { AutocompleteDropdownItem } from './AutocompleteDropdownItem/AutocompleteDropdownItem';
import { AutocompleteDropdownNoOptions } from './AutocompleteDropdownNoOptions/AutocompleteDropdownNoOptions';
import { autocompleteDropdownOptionsFilterStyles as sx } from './AutocompleteDropdownOptions.styles';

type AutoReturn<T> = UseAutocompleteReturnValue<T, false>;

type Props<T> = {
	open: boolean;
	loading?: boolean;
	query?: string;
	isAsync?: boolean;
	getListboxProps: AutoReturn<T>['getListboxProps'];
	getOptionProps: AutoReturn<T>['getOptionProps'];
	renderOption?: (
		option: T,
		optionProps: HTMLAttributes<HTMLLIElement>
	) => ReactNode;
	groupedOptions: AutoReturn<T>['groupedOptions'];
	options?: Array<T> | null;
	anchorEl: HTMLElement | null;
	customSx?: SxProps<Theme>;
};

export const AutocompleteDropdownOptions = <
	T extends DropdownItemBase = DropdownItemBase
>({
	open,
	loading,
	query,
	isAsync,
	getListboxProps,
	getOptionProps,
	renderOption,
	groupedOptions,
	options,
	anchorEl,
	customSx
}: Props<T>) => {
	const [popperWidth, setPopperWidth] = useState(anchorEl?.clientWidth ?? 200);

	useLayoutEffect(() => {
		if (!anchorEl) return;

		const observer = new ResizeObserver(entries => {
			setPopperWidth(entries[0].contentRect.width);
		});
		observer.observe(anchorEl);

		return () => {
			observer.disconnect();
		};
	}, [anchorEl]);

	if (!isAsync && !groupedOptions.length) return null;

	return (
		<Popper
			open={open}
			anchorEl={anchorEl}
			placement='bottom-start'
			sx={{
				width: popperWidth,
				zIndex: POPPER_Z_INDEX
			}}
		>
			<Box
				component='ul'
				{...getListboxProps()}
				sx={mergeSx(sx.dropdown, customSx)}
			>
				{loading && <LinearProgress sx={sx.linearProgress} />}

				{isAsync &&
					options !== null &&
					groupedOptions.length === 0 &&
					query &&
					query.length > 0 && <AutocompleteDropdownNoOptions query={query} />}

				{groupedOptions.map((opt, idx) => {
					const { key, ...optionProps } = getOptionProps({
						option: opt as T,
						index: idx
					});

					if (renderOption) {
						return (
							<Box key={`${key}-${opt.id}`} component='li' {...optionProps}>
								{renderOption(opt as T, optionProps)}
							</Box>
						);
					}

					return (
						<AutocompleteDropdownItem
							key={`${key}-${opt.id}`}
							optionProps={optionProps}
							item={opt}
						/>
					);
				})}
			</Box>
		</Popper>
	);
};
